
import {
    Grid,
    TextField,
    Typography,
    Paper,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/ApiService';
import { useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DownloadIcon from '@mui/icons-material/Download';
import {MenuItem} from '@mui/material';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExcelUpload from '../../../common/ExcelUpload';

const AGNWebexDecommissioningForm = ({
    control,
    watch,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    userRole,
    isUpdateMode,
    updateId
}) => {
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [formData, setFormData] = useState(null);
    const [debtorError, setDebtorError] = useState(null);
    const [isDebtorValid, setIsDebtorValid] = useState(true);
    const [isDebtorTyping, setIsDebtorTyping] = useState(false);
    const [dateError, setDateError] = useState(null);
    const [customerPhoneError, setCustomerPhoneError] = useState(null);
    const [isCustomerPhoneValid, setIsCustomerPhoneValid] = useState(true);
    const [isCustomerPhoneTyping, setIsCustomerPhoneTyping] = useState(false);
    const [countryType, setCountryType] = useState('');
    const [costCentreError, setCostCentreError] = useState(null);
    const [isCostCentreTyping, setIsCostCentreTyping] = useState(false);
    const [uploadedExcelFile, setUploadedExcelFile] = useState(null);
    const location = useLocation();
    const [numberOfSIUsers, setNumberOfSIUsers] = useState(0);
    const [isExcelValid, setIsExcelValid] = useState(false);
    const [existingExcelFileName, setExistingExcelFileName] = useState(null);
    const [existingExcelFileSize, setExistingExcelFileSize] = useState(null);
    const [existingSIUserCount, setExistingSIUserCount] = useState(null);
    const [costDialogOpen, setCostDialogOpen] = useState(false);
    const [estimatedCost, setEstimatedCost] = useState({
        total: 0,
        breakdown: {
            webexDecommissioning: 0
        }
    });
    const fetchWebexCost = async (countryTypeValue, siUsers) => {
        if (!countryTypeValue || !siUsers || siUsers === 0) {
            setEstimatedCost({
                total: 0,
                breakdown: { webexDecommissioning: 0 }
            });
            return;
        }

        try {
            const response = await ApiService.postWebexDecommission('/api/webex-cost/calculate', {
                countryType: countryTypeValue,
                numberOfSIUsers: siUsers
            });

            if (response.success && response.data) {
                setEstimatedCost({
                    total: response.data.totalCost,
                    breakdown: {
                        webexDecommissioning: response.data.totalCost,
                        ...response.data.breakdown
                    }
                });
            }
        } catch (error) {
            console.error('Error calculating Webex cost:', error);
            setDialogTitle('Error');
            setDialogMessage('Failed to calculate cost. Please try again.');
            setDialogOpen(true);
        }
    };

    useEffect(() => {
        const currentCountryType = watch('countryType');
        if (currentCountryType && numberOfSIUsers > 0) {
            fetchWebexCost(currentCountryType, numberOfSIUsers);
        }
    }, [watch('countryType'), numberOfSIUsers]);

    const validatePhoneNumber = (value) => {
        if (!value) return null; // Phone number is optional
        const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,5}[-\s.]?[0-9]{1,9}$/;
        if (!phoneRegex.test(value)) {
            return 'Please enter a valid phone number (e.g., +XX (XXX) XXX-XXXX)';
        }
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length < 7) {
            return 'Phone number must have at least 7 digits';
        }
        if (digitsOnly.length > 15) {
            return 'Phone number cannot exceed 15 digits';
        }
        return null;
    };

    const [isCalculatingCosts, setIsCalculatingCosts] = useState(false);

    const handleAGNSave = async () => {
        setIsCalculatingCosts(true);
        try {
            const validationResult = await trigger();
            if (!validationResult) {
                setIsCalculatingCosts(false);
                setDialogTitle('Validation Error');
                setDialogMessage('Please complete all required fields marked in red before saving.');
                setDialogOpen(true);
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 1000));
            const data = getValues();
            const formattedData = {
                odmTitle: data.odmTitle,
                odmType: 'AGN Webex Decommissioning Request',
                grmDemandNumber: data.grmDemandNumber,
                oeName: data.oeName,
                siteAddress: data.siteAddress,
                city: data.city,
                country: data.country,
                countryType: data.countryType,
                numberOfSIUsers: numberOfSIUsers,
                debtor: data.debtor,
                costCentre: data.costCentre,
                currentSituation: data.currentSituation,
                futureSituation: data.futureSituation,
                desiredDateOfInstallation: data.desiredDateOfInstallation,
                descriptionOfOdmRequest: data.descriptionOfOdmRequest,
                businessReasons: data.businessReasons,
                customerName: data.customerName,
                customerContactMail: data.customerContactMail,
                customerContactPhone: data.customerContactPhone,
                siteTechnicalUserName: data.siteTechnicalUserName,
                siteTechnicalUserEmail: data.siteTechnicalUserEmail,
                siteTechnicalUserPhone: data.siteTechnicalUserPhone,
                requestState: 'Draft',
                requestType: 'AGN Webex Decommissioning Request',
                costEstimate: estimatedCost.total.toString(),
                costEstimateDetails: JSON.stringify(estimatedCost),
                standard: 'Yes'
            };

            setFormData(formattedData);
            setIsCalculatingCosts(false);
            setSaveDialogOpen(true);
        } catch (error) {
            setIsCalculatingCosts(false);
            setDialogTitle('Error');
            setDialogMessage('Error preparing draft: ' + error.message);
            setDialogOpen(true);
        }
    };


    const handleAGNSubmit = async (data) => {
        setIsCalculatingCosts(true);

        try {
            const validationResult = await trigger();
            if (!validationResult || hasValidationErrors()) {
                setIsCalculatingCosts(false);
                setDialogTitle('Validation Error');
                setDialogMessage('Please complete all required fields marked in red before submitting.');
                setDialogOpen(true);
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 1000));

            const formattedData = {
                odmTitle: data.odmTitle,
                odmType: 'AGN Webex Decommissioning Request',
                grmDemandNumber: data.grmDemandNumber,
                oeName: data.oeName,
                siteAddress: data.siteAddress,
                city: data.city,
                country: data.country,
                countryType: data.countryType,
                numberOfSIUsers: numberOfSIUsers,
                debtor: data.debtor,
                costCentre: data.costCentre,
                currentSituation: data.currentSituation,
                futureSituation: data.futureSituation,
                desiredDateOfInstallation: data.desiredDateOfInstallation,
                descriptionOfOdmRequest: data.descriptionOfOdmRequest,
                businessReasons: data.businessReasons,
                customerName: data.customerName,
                customerContactMail: data.customerContactMail,
                customerContactPhone: data.customerContactPhone,
                siteTechnicalUserName: data.siteTechnicalUserName,
                siteTechnicalUserEmail: data.siteTechnicalUserEmail,
                siteTechnicalUserPhone: data.siteTechnicalUserPhone,
                requestState: 'Submitted',
                requestStatus: 'Work in Progress',
                approvalStatus: 'Approved',
                requestType: 'AGN Webex Decommissioning Request',
                submissionDate: new Date().toISOString(),
                costEstimate: estimatedCost.total.toString(),
                costEstimateDetails: JSON.stringify(estimatedCost),
                standard: 'Yes'
            };
            setFormData(formattedData);
            setIsCalculatingCosts(false);
            setCostDialogOpen(true);
        } catch (error) {
            setIsCalculatingCosts(false);
            setDialogTitle('Error');
            setDialogMessage('Error preparing request: ' + error.message);
            setDialogOpen(true);
        }
    };

    const handleCostApprove = async () => {
        setCostDialogOpen(false);
        setIsSubmitting(true);
        try {
            if (!formData) {
                setDialogTitle('Error');
                setDialogMessage('No form data available to submit. Please try again.');
                setDialogOpen(true);
                setIsSubmitting(false);
                return;
            }
            // Check if we have a new file OR an existing file (for update mode)
            if (!uploadedExcelFile && !(isUpdateMode && existingExcelFileName)) {
                setDialogTitle('Error');
                setDialogMessage('Please upload an Excel file before submitting.');
                setDialogOpen(true);
                setIsSubmitting(false);
                return;
            }
            const formDataToSend = new FormData();
            // Only append excel file if a new one was uploaded
            if (uploadedExcelFile) {
                formDataToSend.append('excel_file', uploadedExcelFile);
            }

            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    formDataToSend.append(key, formData[key]);
                }
            });

            if (isUpdateMode && updateId) {
                await ApiService.putWebexDecommission(`/api/webex-decommissioning/${updateId}`, formDataToSend);
            } else {
                await ApiService.postWebexDecommission('/api/webex-decommissioning/', formDataToSend);
            }

            setDialogTitle('Success');
            ApiService.invalidateRequestsCache();

            setDialogMessage('Webex Decommissioning request approved and submitted successfully!');
            setDialogOpen(true);
        } catch (error) {
            setDialogTitle('Error');
            setDialogMessage('Error submitting Webex Decommissioning request: ' + error.message);
            setDialogOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleCostReject = () => {
        setCostDialogOpen(false);
        setDialogTitle('Rejected');
        setDialogMessage('AGN Webex Decommissioning request rejected. You can modify and resubmit later.');
        setDialogOpen(true);
    };
    const handleSaveAsDraft = async () => {
        setSaveDialogOpen(false);
        setIsSaving(true);

        try {
            // Check if we have a new file OR an existing file (for update mode)
            if (!uploadedExcelFile && !(isUpdateMode && existingExcelFileName)) {
                setDialogTitle('Error');
                setDialogMessage('Please upload an Excel file before saving.');
                setDialogOpen(true);
                setIsSaving(false);
                return;
            }
            const formDataToSend = new FormData();
            // Only append excel file if a new one was uploaded
            if (uploadedExcelFile) {
                formDataToSend.append('excel_file', uploadedExcelFile);
            }
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    formDataToSend.append(key, formData[key]);
                }
            });

            if (isUpdateMode && updateId) {
                await ApiService.putWebexDecommission(`/api/webex-decommissioning/${updateId}`, formDataToSend);
                setDialogTitle('Success');
                ApiService.invalidateRequestsCache();

                setDialogMessage('Webex Decommissioning request updated successfully!');
            } else {
                await ApiService.postWebexDecommission('/api/webex-decommissioning/', formDataToSend);
                setDialogTitle('Success');
                ApiService.invalidateRequestsCache();

                setDialogMessage('Webex Decommissioning request saved as draft successfully!');
            }
            setDialogOpen(true);
        } catch (error) {
            setDialogTitle('Error');
            setDialogMessage(`Failed to save Webex Decommissioning draft: ${error.message}`);
            setDialogOpen(true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        if (dialogTitle === 'Success') {
            ApiService.invalidateRequestsCache();
            navigate('/track-requests');
        }
    };

    const hasValidationErrors = () => {
        return (
            (debtorError !== null) ||
            (costCentreError !== null) ||
            (dateError !== null) ||
            (getValues().debtor && !isDebtorValid) ||
            (getValues().customerContactPhone && !isCustomerPhoneValid)
        );
    };

    useEffect(() => {
        const loadWebexDecommissioningData = async () => {
            if (!updateId || !isUpdateMode) return;

            const params = new URLSearchParams(location?.search || '');
            const requestType = params.get('type');

            if (requestType === 'agn_webex') {
                setIsCalculatingCosts(true);
                try {
                    const data = await ApiService.get(`/api/webex-decommissioning/${updateId}`);
                    setValue('odmType', 'AGN Webex Decommissioning Request');
                    setValue('odmTitle', data.odmTitle || data.odm_title || '');
                    setValue('grmDemandNumber', data.grmDemandNumber || data.grm_demand_number || '');
                    setValue('oeName', data.oeName || data.oe_name || '');
                    setValue('siteAddress', data.siteAddress || data.site_address || '');
                    setValue('city', data.city || '');
                    const country = data.country || '';
                    setValue('country', country);
                    if (country === 'Germany') {
                        setValue('countryType', 'German');
                        setCountryType('German');
                    } else if (country) {
                        setValue('countryType', 'Non-German');
                        setCountryType('Non-German');
                    }
                    setValue('costCentre', data.costCentre || data.cost_centre || '');
                    setValue('debtor', data.debtor || '');
                    setValue('currentSituation', data.currentSituation || data.current_situation || '');
                    setValue('futureSituation', data.futureSituation || data.future_situation || '');
                    setValue('desiredDateOfInstallation', data.desiredDateOfInstallation || data.desired_date_of_installation || '');
                    setValue('descriptionOfOdmRequest', data.descriptionOfOdmRequest || data.description_of_odm_request || '');
                    setValue('businessReasons', data.businessReasons || data.business_reasons || '');
                    setValue('customerName', data.customerName || data.customer_name || '');
                    setValue('customerContactMail', data.customerContactMail || data.customer_contact_mail || userRole.userEmail);
                    setValue('customerContactPhone', data.customerContactPhone || data.customer_contact_phone || '');
                    setValue('siteTechnicalUserName', data.siteTechnicalUserName || data.site_technical_user_name || '');
                    setValue('siteTechnicalUserEmail', data.siteTechnicalUserEmail || data.site_technical_user_email || '');
                    setValue('siteTechnicalUserPhone', data.siteTechnicalUserPhone || data.site_technical_user_phone || '');

                    // Load Excel file information and SI user count
                    if (data.excelFileName) {
                        setExistingExcelFileName(data.excelFileName);
                    }
                    if (data.excelFileSize) {
                        setExistingExcelFileSize(data.excelFileSize);
                    }
                    if (data.numberOfSIUsers !== null && data.numberOfSIUsers !== undefined) {
                        setExistingSIUserCount(data.numberOfSIUsers);
                        setNumberOfSIUsers(data.numberOfSIUsers);
                    }

                    setTimeout(() => {
                        trigger();
                    }, 100);

                } catch (error) {
                    console.error('Error loading AGN Webex Decommissioning request:', error);
                    setDialogTitle('Error');
                    setDialogMessage('Failed to load AGN Webex Decommissioning request details. Please try again.');
                    setDialogOpen(true);
                } finally {
                    setIsCalculatingCosts(false);
                }
            }
        };

        loadWebexDecommissioningData();
    }, [updateId, isUpdateMode, location, setValue, userRole.userEmail, trigger]);

    if (isCalculatingCosts && isUpdateMode && !costDialogOpen && !saveDialogOpen && !isSubmitting) {
        const isInitialLoading = !formData || Object.keys(formData).length === 0;

        if (isInitialLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading AGN Webex Decommissioning data...</Typography>
                </Box>
            );
        }
    }
    return (
        <>
             <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#003781', lineHeight: 1 }}>
                SI Users Excel Upload
            </Typography>
            <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => {
                    const link = document.createElement('a');
                    link.href = `${process.env.PUBLIC_URL}/sample_excel/webex_sample.xlsx`;
                    link.download = 'webex_sample.xlsx';
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }}
                sx={{ textTransform: 'none' }}
            >
                Download Sample
            </Button>
        </Box>
         <ExcelUpload
        onValidationChange={setIsExcelValid}
        onUserCountChange={setNumberOfSIUsers}
        onFileChange={setUploadedExcelFile}
        existingFileName={existingExcelFileName}
        existingFileSize={existingExcelFileSize}
        existingSIUserCount={existingSIUserCount}
        requestId={updateId}
        requestType="webex"
    />
  </Paper>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Details of Existing Site
                </Typography>
                 </Box>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Controller
                            name="oeName"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: 'OE Name is required',
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="OE Name *"
                                    fullWidth
                                    placeholder="Allianz Technology"
                                    error={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ||
                                        'Enter a name (e.g., Allianz Technology)'
                                    }
                                />
                            )}
                        />
                    </Grid>
                      <Grid item xs={6}>
      <Controller
          name="country"
          control={control}
          defaultValue=""
          rules={{ required: 'Country is required' }}
          render={({ field, fieldState }) => (
              <TextField
                  {...field}
                  label="Country *"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={countryType === 'German'}
                  InputProps={{
                      readOnly: countryType === 'German'
                  }}
                  sx={{
                      '& .Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.7)',
                      }
                  }}
                  placeholder={countryType === 'Non-German' ? 'Enter country name' : ''}
              />
          )}
      />
  </Grid>
   <Grid item xs={6}>
      <Controller
          name="countryType"
          control={control}
          defaultValue=""
          rules={{ required: 'Country Type is required' }}
          render={({ field, fieldState }) => (
              <TextField
                  {...field}
                  select
                  label="Country Type *"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      setCountryType(value);
                      if (value === 'German') {
                          setValue('country', 'Germany');
                      } else {
                          setValue('country', '');
                      }
                  }}
                  style={{ minWidth: '200px' }}
                      SelectProps={{
                          MenuProps: {
                              PaperProps: {
                                  style: { maxHeight: 300, width: 'auto' }
                              }
                          }
                      }}
              >
                  <MenuItem value="">Select Country Type</MenuItem>
                  <MenuItem value="German">German</MenuItem>
                  <MenuItem value="Non-German">Non-German</MenuItem>
              </TextField>
          )}
      />
  </Grid>
                      <Grid item xs={6}>
                        <Controller
                                                    name="siteAddress"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field, fieldState }) => (
                                                        <TextField
                                                            {...field}
                                                            label="Location"
                                                            fullWidth
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message || 'Enter the site location (optional)'}
                                                        />
                                                    )}
                                                />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="debtor"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: 'Debtor is required',
                                pattern: {
                                    value: /^\d{10}$/,
                                    message: 'Debtor must be exactly 10 digits'
                                }
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Debtor *"
                                    fullWidth
                                    placeholder="1234567890"
                                    error={!!fieldState.error || !!debtorError}
                                    helperText={
                                        fieldState.error?.message ||
                                        debtorError ||
                                        (isDebtorTyping ? 'Continue typing...' : 'Enter a 10-digit number')
                                    }
                                    inputProps={{
                                        inputMode: 'numeric',
                                        maxLength: 10
                                    }}
                                    onBlur={() => {
                                        setIsDebtorTyping(false);
                                        const value = field.value;
                                        if (value) {
                                            if (!/^\d{10}$/.test(value)) {
                                                setDebtorError('Debtor must be exactly 10 digits');
                                                setIsDebtorValid(false);
                                            } else {
                                                setDebtorError(null);
                                                setIsDebtorValid(true);
                                            }
                                        } else {
                                            setDebtorError('Debtor is required');
                                            setIsDebtorValid(false);
                                        }
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        const truncatedValue = value.slice(0, 10);
                                        field.onChange(truncatedValue);
                                        setIsDebtorTyping(true);

                                        if (value) {
                                            if (!/^\d+$/.test(value)) {
                                                setDebtorError('Debtor must contain only digits');
                                                setIsDebtorValid(false);
                                            } else if (value.length < 10) {
                                                setDebtorError(`Debtor must be 10 digits (currently ${value.length})`);
                                                setIsDebtorValid(false);
                                            } else if (value.length > 10) {
                                                setDebtorError('Debtor cannot exceed 10 digits');
                                                setIsDebtorValid(false);
                                            } else {
                                                setDebtorError(null);
                                                setIsDebtorValid(true);
                                            }
                                        } else {
                                            setDebtorError(null);
                                            setIsDebtorValid(false);
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="costCentre"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: 'Cost Centre is required',
                                pattern: {
                                    value: /^\d{10}$/,
                                    message: 'Cost Centre must be exactly 10 digits'
                                }
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Cost Centre *"
                                    fullWidth
                                    placeholder="1234567890"
                                    inputProps={{
                                        inputMode: 'numeric',
                                        maxLength: 10
                                    }}
                                    error={!!fieldState.error || !!costCentreError}
                                    helperText={
                                        fieldState.error?.message ||
                                        costCentreError ||
                                        (isCostCentreTyping ? 'Continue typing...' : 'Enter a 10-digit numeric Cost Centre')
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value && !/^\d*$/.test(value)) {
                                            e.target.value = value.replace(/\D/g, '');
                                        }

                                        field.onChange(e);
                                        setIsCostCentreTyping(true);
                                        if (value && !/^\d+$/.test(value)) {
                                            setCostCentreError('Cost Centre must contain only digits');
                                        } else if (value && value.length < 10) {
                                            setCostCentreError(`Cost Centre must be exactly 10 digits (${value.length}/10)`);
                                        } else if (value && value.length > 10) {
                                            setCostCentreError('Cost Centre cannot exceed 10 digits');
                                        } else {
                                            setCostCentreError(null);
                                        }

                                        setTimeout(() => setIsCostCentreTyping(false), 1500);
                                    }}
                                    onKeyPress={(e) => {
                                        if (!/[\d]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onBlur={() => {
                                        field.onBlur();
                                        setIsCostCentreTyping(false);
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Paper>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Technical Details
                </Typography>
                <Grid container spacing={3}>
                      <Grid item xs={6}>
      <Controller
          name="desiredDateOfInstallation"
          control={control}
          defaultValue=""
          rules={{
              required: 'Desired Date of Completion is required',
              validate: {
                  futureDate: value => {
                      const today = new Date();
                      const selectedDate = new Date(value);
                      today.setHours(0, 0, 0, 0);
                      selectedDate.setHours(0, 0, 0, 0);
                      const minDate = new Date(today);
                      minDate.setDate(today.getDate() + 30);

                      if (selectedDate < minDate) {
                          return `Date must be at least 30 days in the future as per SLA (minimum: ${minDate.toLocaleDateString()})`;
                      }
                      return true;
                  }
              }
          }}
          render={({ field, fieldState }) => (
              <TextField
                  {...field}
                  label="Desired Date of Completion *"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!fieldState.error || !!dateError}
                  helperText={
                      fieldState.error?.message ||
                      dateError ||
                      'Date must be at least 30 days in the future (SLA requirement)'
                  }
                  inputProps={{
                      min: (() => {
                          const minDate = new Date();
                          minDate.setDate(minDate.getDate() + 30);
                          return minDate.toISOString().split('T')[0];
                      })()
                  }}
                  onChange={(e) => {
                      field.onChange(e);
                      const value = e.target.value;
                      if (value) {
                          const today = new Date();
                          const selectedDate = new Date(value);
                          today.setHours(0, 0, 0, 0);
                          selectedDate.setHours(0, 0, 0, 0);
                          const minDate = new Date(today);
                          minDate.setDate(today.getDate() + 30);

                          if (selectedDate < minDate) {
                              const formattedMinDate = minDate.toLocaleDateString();
                              setDateError(`Date must be at least 30 days in the future per SLA (minimum: ${formattedMinDate})`);
                          } else {
                              setDateError(null);
                          }
                      } else {
                          setDateError('Desired Date of Completion is required');
                      }
                  }}
              />
          )}
      />
  </Grid>

                </Grid>
            </Paper>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Request Description
                </Typography>
                <Controller
                    name="descriptionOfOdmRequest"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Description is required' }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            label="Description of ODM Request *"
                            multiline
                            rows={4}
                            fullWidth
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            variant="outlined"
                            sx={{
                                mb: 2,
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    width: '100%'
                                }
                            }}
                        />
                    )}
                />
            </Paper>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Business Reasons
                </Typography>
                <Controller
                    name="businessReasons"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Business Reasons is require' }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            label="Business Reasons *"
                            multiline
                            rows={4}
                            fullWidth
                            variant="outlined"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            sx={{
                                mb: 2,
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    width: '100%'
                                }
                            }}
                        />
                    )}
                />
            </Paper>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003781' }}>
                    Customer Contact
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Controller
                            name="customerName"
                            control={control}
                            defaultValue=""
                            rules={{ required: 'Customer Name is required' }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Name *"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="customerContactMail"
                            control={control}
                            defaultValue={userRole?.userEmail || ''}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Email *"
                                    type="email"
                                    fullWidth
                                    disabled={true}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    sx={{
                                        '& .Mui-disabled': {
                                            backgroundColor: '#f5f5f5',
                                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.7)',
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="customerContactPhone"
                            control={control}
                            defaultValue=""
                            rules={{
                                validate: (value) => {
                                    const error = validatePhoneNumber(value);
                                    return error ? error : true;
                                }
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Customer Contact Phone"
                                    fullWidth
                                    placeholder="+XX (XXX) XXX-XXXX (Optional)"
                                    error={!!fieldState.error || !!customerPhoneError}
                                    helperText={
                                        fieldState.error?.message ||
                                        customerPhoneError ||
                                        (isCustomerPhoneTyping ? 'Continue typing...' : 'Format: +XX (XXX) XXX-XXXX (Optional)')
                                    }
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setIsCustomerPhoneTyping(true);

                                        const error = validatePhoneNumber(e.target.value);
                                        setCustomerPhoneError(error);
                                        setIsCustomerPhoneValid(error ? false : true);
                                    }}
                                    onBlur={() => {
                                        field.onBlur();
                                        setIsCustomerPhoneTyping(false);

                                        // Phone is optional, so empty value is valid
                                        if (!field.value) {
                                            setCustomerPhoneError(null);
                                            setIsCustomerPhoneValid(true);
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Dialog open={isCalculatingCosts} disableBackdropClick={true} disableEscapeKeyDown={true}>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6">Work in Progress...</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Wait for Few Moments
                    </Typography>
                </DialogContent>
            </Dialog>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
                mb: 2,
                gap: 2,
                '& .MuiButton-root': {
                    minWidth: 100,
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    borderRadius: 1.5,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover:not(:disabled)': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                    }
                }
            }}>
                    <Button
      variant="contained"
      color="primary"
      type="button"
      onClick={handleAGNSave}
      disabled={isSaving || isCalculatingCosts}
      startIcon={(isSaving || isCalculatingCosts) ? <CircularProgress size={20} /> : null}
  >
      {(isSaving || isCalculatingCosts) ? 'Processing...' : 'Save as Draft'}
  </Button>

        <Button
            variant="outlined"
            color="secondary"
            type="button"
            onClick={() => navigate('/')}
            disabled={isSaving || isSubmitting || isCalculatingCosts}
        >
           Cancel
            </Button>

  <Button
      type="button"
      variant="contained"
      color="primary"
      onClick={handleSubmit(
          handleAGNSubmit,
          (errors) => {
              setIsCalculatingCosts(false);
              setDialogTitle('Validation Error');
              setDialogMessage('Please complete all required fields marked in red before submitting.');
              setDialogOpen(true);
              console.error('Validation errors:', errors);
          }
      )}
      disabled={isSubmitting || hasValidationErrors() || isCalculatingCosts || !isExcelValid}  // Add !isExcelValid
      startIcon={(isSubmitting || isCalculatingCosts) ? <CircularProgress size={20} /> : null}
  >
      {(isSubmitting || isCalculatingCosts) ? 'Processing...' : 'Submit'}
  </Button>

            </Box>

            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography>{dialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
                <DialogTitle>Save as Draft?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Your AGN Webex Decommissioning request will be saved as a draft and can be completed later.
                        {!isUpdateMode && " You'll be able to find it in the Track Requests page."}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveAsDraft}
                        color="primary"
                        variant="contained"
                        disabled={isSaving}
                    >
                        Save as Draft
                        {isSaving && <CircularProgress size={20} sx={{ ml: 1 }} />}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isSubmitting} disableBackdropClick={true} disableEscapeKeyDown={true}>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography>Submitting your AGN Webex Decommissioning request...</Typography>
                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                        This may take a few moments
                    </Typography>
                </DialogContent>
            </Dialog>

  <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
          <Typography variant="h6" component="div">
              Webex Decommission Cost Approval
          </Typography>
      </DialogTitle>
      <DialogContent>
          <Box sx={{ mb: 2 }}>
              {/* Total Cost */}
              <Typography variant="h6" gutterBottom>
                  Total Cost: €{estimatedCost?.total?.toFixed(2)}
              </Typography>

              <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>View Cost Breakdown</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                              <TableBody>
                                  <TableRow>
                                      <TableCell component="th" scope="row">
                                          Webex Decommission Cost (910530)

                                      </TableCell>
                                      <TableCell align="right">€{estimatedCost?.breakdown?.webexDecommissioning?.toFixed(2)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                      <TableCell component="th" scope="row">
                                          Webconf Individual Service - 1 time processing charge (910530)
                                          <Tooltip title="effort for webex team" arrow placement="right">
                                              <HelpOutlineIcon
                                                  fontSize="small"
                                                  sx={{
                                                      ml: 0.5,
                                                      color: 'text.secondary',
                                                      fontSize: '16px',
                                                      cursor: 'help',
                                                      '&:hover': { color: 'primary.main' }
                                                  }}
                                              />
                                          </Tooltip>
                                      </TableCell>
                                      <TableCell align="right">€{estimatedCost?.breakdown?.webconfIndividualService?.toFixed(2) || '810.35'}</TableCell>
                                  </TableRow>
                                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                          Total Cost
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                          €{estimatedCost?.total?.toFixed(2)}
                                      </TableCell>
                                  </TableRow>
                              </TableBody>
                          </Table>
                      </TableContainer>
                  </AccordionDetails>
              </Accordion>
          </Box>

          <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500, mt: 2 }}>
              Do you want to approve and submit this Webex Decommission request?
          </Typography>
      </DialogContent>
      <DialogActions>
          <Button onClick={handleCostReject} color="secondary">
              Reject
          </Button>
          <Button
              onClick={handleCostApprove}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
              Approve & Submit
          </Button>
      </DialogActions>
  </Dialog>


        </>
    );
};

export default AGNWebexDecommissioningForm;
